import { normalize } from "normalizr";
import { all, call, put, takeLatest } from "redux-saga/effects";

import {
    BLOGS_SHORT_MESSAGE_DETAIL_FETCH_TRIGGER,
    BlogsShortMessageDetailFetchNormalizedResponse,
    BlogsShortMessageDetailFetchTriggerAction,
    blogsShortMessageDetailFetchApi,
} from "@/mk2/apps/blogs/containers/BlogsShortMessageDetail/BlogsShortMessageDetail.actions";
import {
    CommentsFetchNormalizedResponse,
    commentsFetchApi,
} from "@/mk2/apps/blogs/containers/Comments/Comments.actions";
import { getCommentParent } from "@/mk2/apps/blogs/helpers";
import { XHRAction, XHRGetError, handleXHRGetErrorSaga, normalizeError } from "@/mk2/helpers/api";
import { mergeDenormalized } from "@/mk2/helpers/denormalized";
import { getLogger } from "@/mk2/logger";
import { BlogPostEntity, BlogPostSchema, CommentEntity, CommentSchema } from "@/mk2/schemas";
import { getDenormalizedEntity } from "@/mk2/selectors";
import { HistoryAction } from "@/mk2/services/browserHistory";
import { blogs_api_short_message_detail_url } from "@/mk/urls/functions";

const logger = getLogger("blogs/BlogsShortMessageDetail.sagas");

interface ApiResponseBlogsShortMessageDetail {
    body: {
        entities: BlogsShortMessageDetailFetchNormalizedResponse["entities"];
        result: {
            post: number;
            complaints: number[];
        };
        comments: CommentEntity[];
        canAdd: boolean;
        hasHiddenText: boolean;
    };
}

function* fetchBlogsShortMessageDetail({
    username,
    postSlug,
    accessKey,
    xhr,
    history,
    routeNavigate,
}: BlogsShortMessageDetailFetchTriggerAction & XHRAction & HistoryAction) {
    yield put(blogsShortMessageDetailFetchApi.request(username, postSlug, accessKey));

    try {
        const response: ApiResponseBlogsShortMessageDetail = yield call(() =>
            xhr.get(blogs_api_short_message_detail_url(), { username, postSlug, accessKey }),
        );

        // TODO: Remove when comments will be normalized on server
        const normalizedResponse: BlogsShortMessageDetailFetchNormalizedResponse = normalize(
            response.body,
            {
                comments: [CommentSchema],
            },
        );

        // TODO: Remove when all entities will be normalized on server
        const mergedResponse = mergeDenormalized(normalizedResponse, {
            entities: response.body.entities,
            result: response.body.result,
        });

        const post = getDenormalizedEntity<BlogPostEntity>(
            response.body,
            BlogPostSchema,
            response.body.result.post,
        );

        // integrate loaded comments into Comments container
        const commentsNormalizedResponse: CommentsFetchNormalizedResponse = {
            result: {
                comments: normalizedResponse.result.comments,
                canAdd: response.body.canAdd,
                hasHiddenText: response.body.hasHiddenText,
            },
            entities: normalizedResponse.entities,
        };
        yield put(commentsFetchApi.success(getCommentParent(post), commentsNormalizedResponse));

        yield put(
            blogsShortMessageDetailFetchApi.success(username, postSlug, accessKey, mergedResponse),
        );
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, history, logger);
        yield put(
            blogsShortMessageDetailFetchApi.failure(
                username,
                postSlug,
                accessKey,
                normalizeError(error),
            ),
        );
    }
}

export default function* root() {
    yield all([takeLatest(BLOGS_SHORT_MESSAGE_DETAIL_FETCH_TRIGGER, fetchBlogsShortMessageDetail)]);
}
